import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, Grid, GridCell, Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { CardElement, injectStripe } from 'react-stripe-elements'
import BillingCreditCardIcon from 'assets/icons/billingCreditCard'
import TicketIcon from 'assets/icons/ticket'
import { Input } from 'src/components/ConnectedForm'
import { useCalculateOrderTotals } from 'src/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import OrderSummary from './OrderSummary'
import ProductListItem from './ProductListItem'

const JoinPaymentForm = ({
   currentStepNumber,
   initialPaymentFormValues,
   onContinue,
   onGoBack,
   numTotalSteps,
   stripe
}) => {
   const { initiative } = useContext(InitiativeHierarchyContext)
   const products = initiative?.commerce?.products || []

   const [selectedProducts, setSelectedProducts] = useState(() => {
      if (initialPaymentFormValues.products) {
         return products.filter(product => initialPaymentFormValues.products.includes(product.id))
      }

      return []
   })

   // TODO: Implement Discounts. See PR #276.
   const { orderTotals, loading: orderTotalsLoading } = useCalculateOrderTotals({
      initiativeId: initiative.id,
      discountCodes: [],
      productIds: selectedProducts.map(p => p.id)
   })

   const handleProductSelection = (productId) => {
      const selectedProduct = products.find(product => product.id === productId)
      setSelectedProducts([selectedProduct])
   }

   const handleContinue = async (values, actions) => {
      try {
         if (orderTotals.total > 0) {
            const { token } = await stripe.createToken({ name: values.billingName })
            if (!token) {
               throw new Error('Error validating billing information, please ensure you entered your credit card information correctly')
            }

            values.paymentTokenId = token.id
         }

         await onContinue(values)
      } catch (err) {
         // TODO: Implement Error Notification
         window.alert(err.message)
      }

      actions.setSubmitting(false)
   }

   // We don't want to allow the user to click the "Continue" button
   // until the order totals have loaded, since it may change if they
   // need to enter any billing details before proceeding.
   const submitButtonMode = orderTotalsLoading
      ? 'disabled'
      : 'normal'

   return (
      <Formik
         initialValues={initialPaymentFormValues}
         onSubmit={handleContinue}
         render={({ handleSubmit, isSubmitting, values }) => (
            <Form
               elementAppearance="plain"
               elementBackgroundColor="#eee"
               elementFocusBackgroundColor="#f3f3f3"
               onSubmit={handleSubmit}>
               <View
                  position="absolute"
                  top="0px"
                  left="0px"
                  width="100%"
                  height="100%"
                  scroll="on[a-b]">
                  <View
                     width="100%"
                     height="calc(100% - 72px)[c-f]"
                     scroll="on[c-f]">
                     <View
                        display="flex"
                        alignItems="center"
                        width="100%"
                        minHeight="90px"
                        padding="24px 18px[a] 24px 30px[b-f]"
                        borderBottom="1px solid #eee">
                        <View>
                           <Text size="4" weight="medium">
                              {initiative.name}
                           </Text>
                           <Text size="2" color="#888">
                              {`Step ${currentStepNumber} of ${numTotalSteps} `}
                              &mdash;
                              Choose your ticket
                           </Text>
                        </View>
                     </View>
                     <View width="100%" padding="18px[a] 24px 30px[b-f]">
                        <Grid columns="1" spacing="12px">
                           <GridCell>
                              <View display="flex" flexFlow="column[a] row[b-f]">
                                 <View
                                    flex="0 0 auto"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    backgroundColor="#000"
                                    borderRadius="50%"
                                    width="30px"
                                    height="30px"
                                    marginRight="18px">
                                    <TicketIcon
                                       width="18px"
                                       height="18px"
                                       strokeWidth="2px"
                                       color="#fff"
                                    />
                                 </View>
                                 <View flex="1 1 auto" padding="6px 0 0 0">
                                    <Text size="2" weight="medium">
                                       Choose your ticket
                                    </Text>
                                    <Spacer size="2" />
                                    <View
                                       width="100%"
                                       backgroundColor="#f8f8f8"
                                       borderRadius="6px"
                                       padding="0px 18px">
                                       <View width="100%">
                                          {products.map((product, index) => (
                                             <View key={product.id} width="100%">
                                                <ProductListItem
                                                   borderStyle={index === 0 ? 'none' : undefined}
                                                   onChange={handleProductSelection}
                                                   product={product}
                                                />
                                             </View>
                                          ))}
                                       </View>
                                    </View>
                                 </View>
                              </View>
                           </GridCell>
                           {/* Order Summary & Discounts */}
                           {!orderTotalsLoading && orderTotals.total > 0 && (
                              <GridCell>
                                 <View width="100%" paddingLeft="48px[b-f]">
                                    <OrderSummary
                                       loading={orderTotalsLoading}
                                       orderTotals={orderTotals}
                                    />
                                 </View>
                                 {/* discounts.length === 0 && (
                                    <ButtonGroup>
                                       <Button
                                          icon="ion-android-arrow-dropright-circle"
                                          color="#2bb99b"
                                          onClick={() => this.handleToggleDiscountModal(true)}
                                          name="Apply Discount Code"
                                          size="small"
                                          translucent
                                       />
                                    </ButtonGroup>
                                 ) */}
                              </GridCell>
                           )}
                           {/* Billing Information */}
                           {!orderTotalsLoading && orderTotals.total > 0 && (
                              <GridCell>
                                 <View display="flex" flexFlow="column[a] row[b-f]">
                                    <View
                                       flex="0 0 auto"
                                       display="flex"
                                       justifyContent="center"
                                       alignItems="center"
                                       backgroundColor="#000"
                                       borderRadius="50%"
                                       width="30px"
                                       height="30px"
                                       marginRight="18px">
                                       <BillingCreditCardIcon
                                          width="18px"
                                          height="18px"
                                          strokeWidth="2px"
                                          color="#fff"
                                       />
                                    </View>
                                    <View flex="1 1 auto" padding="6px 0 0 0">
                                       <Text size="2" weight="medium">
                                          Enter Billing Information
                                       </Text>
                                       <Spacer size="2" />
                                       <View
                                          width="100%"
                                          backgroundColor="#f8f8f8"
                                          borderRadius="6px"
                                          padding="24px">
                                          <Text weight="medium" size="2">
                                             Credit Card Information
                                          </Text>
                                          <Spacer size="1" />
                                          <View width="100%" backgroundColor="#eee" padding="9px">
                                             <CardElement style={{ base: { fontSize: '14px' } }} />
                                          </View>
                                          <Spacer size="2" />
                                          <Text weight="medium" size="1.5">Billing Name</Text>
                                          <Spacer size="1" />
                                          <Input required name="billingName" />
                                          <Spacer size="2" />
                                          <Text weight="medium" size="1.5">Billing Email Address</Text>
                                          <Spacer size="1" />
                                          <Input required name="billingEmail" />
                                          <Spacer size="1" />
                                          <Text size="1" color="#888">
                                             Your receipt will be sent to this email address
                                          </Text>
                                       </View>
                                    </View>
                                 </View>
                              </GridCell>
                           )}
                        </Grid>
                     </View>
                  </View>
                  <View
                     display="flex"
                     justifyContent="flex-end"
                     alignItems="center"
                     position="absolute[c-f]"
                     bottom="0px[c-f]"
                     left="0px[c-f]"
                     width="100%"
                     height="72px"
                     padding="0px 18px[a] 0px 24px[b-f]"
                     borderTop="1px solid #eee">
                     <View>
                        <Button
                           onClick={() => onGoBack(values)}
                           color="#eee"
                           textColor="#444"
                           size="md"
                           mode={(isSubmitting || orderTotalsLoading) ? 'disabled' : 'normal'}
                           name="Previous"
                        />
                        <Button
                           type="submit"
                           color="#35b865"
                           size="md"
                           mode={isSubmitting ? 'loading' : submitButtonMode}
                           name={currentStepNumber === numTotalSteps
                              ? 'Register'
                              : 'Continue'
                           }
                        />
                     </View>
                  </View>
               </View>
            </Form>
         )}
      />
   )
}

JoinPaymentForm.propTypes = {
   currentStepNumber: PropTypes.number.isRequired,
   initialPaymentFormValues: PropTypes.object,
   onGoBack: PropTypes.func.isRequired,
   onContinue: PropTypes.func.isRequired,
   numTotalSteps: PropTypes.number.isRequired,
   stripe: PropTypes.object
}

JoinPaymentForm.defaultProps = {
   initialPaymentFormValues: {},
   stripe: null
}

export default injectStripe(JoinPaymentForm)
